import React, { useCallback, useEffect, useState, startTransition } from "react";
import EmpresaListTable from "../organisms/empresaList/EmpresaListTable";
import { GET_ENTERPRISES } from "../../api/apiEmpresas";
import { GET_ACCOUNTANTS, GET_ACCOUNTANTS_BY_ADMIN_ID, GET_ADMINS } from "../../api/apiAdmin";
import EmpresaListSearch from "../organisms/empresaList/EmpresaListSearch";
import CardTables from "../atoms/CardTables";
import SubHeading from "../atoms/SubHeading";
import EmpresaListStats from "../organisms/empresaList/EmpresaListStats";
import Fuse from "fuse.js";
import CardHeader from "../atoms/CardHeader";
import Breadcrumb from "../molecules/Breadcrumb";
import * as XLSX from "xlsx";
import Button from "../atoms/Button";
import { getResponsibleName } from "../../functions/getNames";
import { FaFileExcel } from "react-icons/fa";

const EmpresaListTemplate = ({ user }) => {
    const [empresas, setEmpresas] = useState();
    const [admins, setAdmins] = useState();
    const [accountants, setAccountants] = useState();
    const [filteredEmpresas, setFilteredEmpresas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({ empresa: "", rfc: "", contador: "", regimenFiscal: [] });

    const getEnterprises = useCallback ( async () => {
        setLoading(true);
        let accountantsData;

        try {
            if (user?.roleInfo?.isSuperAdmin === true) {
                accountantsData = await GET_ACCOUNTANTS();
            } else {
                accountantsData = await GET_ACCOUNTANTS_BY_ADMIN_ID(user?.roleInfo?.id);
            }
            setAccountants(accountantsData);
        } catch (error) {
            console.error("Error al obtener contadores:", error);
        }

        try {
            const adminsData = await GET_ADMINS();
            setAdmins(adminsData);
        } catch (errorAdmins) {
            console.error(errorAdmins);
        }

        try {
            const enterprisesData = await GET_ENTERPRISES();
            let filteredEnterprises = enterprisesData;

            if (user.role === "Admin" && user?.roleInfo?.isSuperAdmin !== true) {
                const validAccountantIds = accountantsData.map((a) => a.id);
                filteredEnterprises = enterprisesData.filter((empresa) =>
                    validAccountantIds.includes(empresa.accountantId)
                );
            }

            const sortedData = filteredEnterprises.sort((a, b) => a.id - b.id);
            setEmpresas(sortedData);
            setFilteredEmpresas(sortedData);
        } catch (error) {
            console.error("Error al obtener empresas:", error);
        }

        setLoading(false);
    }, [user]);

	useEffect(() => {
        getEnterprises();
    }, [getEnterprises]);

    useEffect(() => {
        if (!empresas || !accountants) return;

        const enrichedEmpresas = empresas.map((empresa) => {
            const accountant = accountants.find(a => a.id === empresa.accountantId);
            return {
                ...empresa,
                contadorName: accountant ? accountant.name : "",
            };
        });

        const regimenFiltered = filters.regimenFiscal.length > 0
        ? enrichedEmpresas.filter((empresa) => {
            const regimenes = empresa.c_RegimenFiscal?.split(",").map(r => r.trim()) || [];
            console.log("Empresa:", empresa.name, "Regímenes:", regimenes, "Filtro:", filters.regimenFiscal);
            return filters.regimenFiscal.some(filtro =>
                regimenes.map(r => r.toString()).includes(filtro.toString())
            );
        })
        : enrichedEmpresas;

        const fuse = new Fuse(regimenFiltered, {
            keys: [
                { name: "name", weight: 0.5 },
                { name: "rfc", weight: 0.3 },
                { name: "contadorName", weight: 0.2 },
            ],
            threshold: 0.3,
        });

        const searchTerms = `${filters.empresa} ${filters.rfc} ${filters.contador}`.trim();

        startTransition(() => {
            if (searchTerms) {
                const results = fuse.search(searchTerms).map(result => result.item);
                setFilteredEmpresas(results);
            } else {
                setFilteredEmpresas(regimenFiltered);
            }
        });

    }, [filters, empresas, accountants]);

    const downloadExcel = () => {
        const sortedData = [...filteredEmpresas].sort((a, b) => {
            const nameA = getResponsibleName(a, accountants, admins)?.toLowerCase() || "";
            const nameB = getResponsibleName(b, accountants, admins)?.toLowerCase() || "";
            return nameA.localeCompare(nameB);
        });

        const dataToExport = sortedData.map(emp => {
            return {
                "Nombre": emp.name,
                "Tipo": emp.type === "LegalEntitie" ? "Persona MORAL" : "Persona FISICA",
                "Nombre Representante Legal": emp.name_rl || "N/A",
                "Contador": getResponsibleName(emp, accountants, admins) || "Sin Contador Asignado"
            };
        });

        const worksheet = XLSX.utils.json_to_sheet(dataToExport);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Empresas");
        XLSX.writeFile(workbook, "Reporte_Empresas.xlsx");
    };

    return (
        <div className="space-y-2">
            <CardHeader>
                <Breadcrumb
                    items={[
                        { label: "Inicio", to: "/dashboard" },
                        { label: "Empresas", to: null },
                    ]}
                />
                <Button 
                    text={
                        <div className="flex items-center gap-2">
                            <FaFileExcel size={20} />
                            <span>Descargar Reporte</span>
                        </div>
                    } 
                    bgColor="green" 
                    onClick={downloadExcel} 
                />
            </CardHeader>
            <CardTables>
                <EmpresaListSearch filters={filters} setFilters={setFilters} />
                <EmpresaListTable data={filteredEmpresas} loading={loading} accountants={accountants} admins={admins} refetch={getEnterprises} />
            </CardTables>
            {user?.roleInfo?.isSuperAdmin === true &&
                <>
                    <SubHeading text="Estadísticas" color="blue" />
                    <EmpresaListStats data={filteredEmpresas} loading={loading} />
                </>
            }
        </div>
    );
};

export default EmpresaListTemplate;